import { useMemo } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { SectionTitle } from "../components/SectionTitle";
import { PackageCard, type PackageCardFeature } from "../components/PackageCard";
import { LoadingContainer } from "../components/LoadingContainer";
import { useGetResellerAvailableBillingItemsQuery } from "../query/resellerAvailableBillingItemsQuery";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${fontSizes.xlarge}px;
  font-weight: 600;
  color: ${colors.text};
`;

const Intro = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  line-height: 1.5;
  max-width: 720px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${spacing.x2}px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const EmptyMessage = styled.p`
  margin: 0;
  color: ${colors.textMuted};
  text-align: center;
`;

const featureOrder = Object.values(Features);

const getFeatureSortIndex = (feature: Features) => {
  const index = featureOrder.indexOf(feature);
  return index === -1 ? featureOrder.length : index;
};

const getTranslatedFeatureName = (
  feature: Features,
  t: (key: string) => string,
) => {
  const key = `availableBillingItemForm.feature.${feature}`;
  const translated = t(key);
  return translated !== key ? translated : feature;
};

const sortBillingItems = (items: RestAvailableBillingItem[]) =>
  items.slice().sort((a, b) => a.name.localeCompare(b.name, "sv"));

export const PriceListView = () => {
  const { t } = useTranslation();
  const { resellerId } = useParams<{ resellerId: string }>();

  const {
    data: billingItems,
    isLoading,
    error,
  } = useGetResellerAvailableBillingItemsQuery(resellerId ?? null);

  const packages = useMemo(() => {
    if (!billingItems) {
      return [];
    }

    return sortBillingItems(
      billingItems.filter((item) => item.type === BillingItemType.PACKAGE),
    );
  }, [billingItems]);

  const addons = useMemo(() => {
    if (!billingItems) {
      return [];
    }

    return sortBillingItems(
      billingItems.filter((item) => item.type === BillingItemType.ADD_ON),
    );
  }, [billingItems]);

  const getFeatureItems = (item: RestAvailableBillingItem): PackageCardFeature[] => {
    if (item.features && item.features.length > 0) {
      return item.features
        .slice()
        .sort((a, b) => getFeatureSortIndex(a) - getFeatureSortIndex(b))
        .map((feature) => ({
          label: getTranslatedFeatureName(feature, t),
          tooltip: t(`priceList.featureDescription.${feature}`),
        }));
    }

    return item.bullets ?? [];
  };

  const renderBillingItemCard = (item: RestAvailableBillingItem) => (
    <PackageCard
      key={item.id}
      id={item.id}
      name={item.name}
      description={item.description}
      features={getFeatureItems(item)}
      price={item.pricing}
      selected={false}
      onSelect={() => undefined}
      selectionType="radio"
      comingSoon={item.comingSoon}
      readOnly
    />
  );

  if (isLoading) {
    return (
      <Container>
        <PageHeader>
          <Title>{t("priceList.title")}</Title>
        </PageHeader>
        <LoadingContainer message={t("priceList.loading")} />
      </Container>
    );
  }

  const hasItems = packages.length > 0 || addons.length > 0;

  return (
    <Container>
      <PageHeader>
        <Title>{t("priceList.title")}</Title>
        <Intro>{t("priceList.intro")}</Intro>
      </PageHeader>

      {error && (
        <Card>
          <EmptyMessage>{t("priceList.error")}</EmptyMessage>
        </Card>
      )}

      {!error && !hasItems && (
        <Card>
          <EmptyMessage>{t("priceList.empty")}</EmptyMessage>
        </Card>
      )}

      {!error && packages.length > 0 && (
        <Section>
          <SectionTitle>{t("priceList.packagesSection")}</SectionTitle>
          <CardsGrid>{packages.map(renderBillingItemCard)}</CardsGrid>
        </Section>
      )}

      {!error && addons.length > 0 && (
        <Section>
          <SectionTitle>{t("priceList.addonsSection")}</SectionTitle>
          <CardsGrid>{addons.map(renderBillingItemCard)}</CardsGrid>
        </Section>
      )}
    </Container>
  );
};
